import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Trash2,
  GripVertical,
  Play,
  Upload,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { useStore } from '../../stores/useStore';
import { Settings, FileText as FileTextIcon } from 'lucide-react';
import { analyzePapers } from '../../lib/analyzer';
import type { Keyword } from '../../types';
import clsx from 'clsx';

// Sortable keyword item
function SortableKeyword({
  keyword,
  onRemove,
  onUpdate,
}: {
  keyword: Keyword;
  onRemove: () => void;
  onUpdate: (text: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(keyword.text);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: keyword.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSave = () => {
    if (editText.trim()) {
      onUpdate(editText.trim());
    }
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-slate-800',
        'border border-slate-200 dark:border-slate-700',
        'transition-shadow duration-200',
        isDragging && 'shadow-lg ring-2 ring-primary-500 opacity-90'
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {isEditing ? (
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          className="flex-1 px-2 py-1 text-sm rounded border border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
        />
      ) : (
        <span
          onClick={() => setIsEditing(true)}
          className="flex-1 text-sm text-slate-700 dark:text-slate-300 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400"
        >
          {keyword.text}
        </span>
      )}

      {keyword.matchCount !== undefined && (
        <span className="text-xs text-slate-400 dark:text-slate-500">
          ({keyword.matchCount})
        </span>
      )}

      <button
        onClick={onRemove}
        className="p-1 text-slate-400 hover:text-danger-500 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// Keyword list component
function KeywordList({
  title,
  keywords,
  type,
  color,
}: {
  title: string;
  keywords: Keyword[];
  type: 'inclusion' | 'exclusion';
  color: 'accent' | 'danger';
}) {
  const { addKeyword, removeKeyword, updateKeyword, reorderKeywords, clearKeywords } = useStore();
  const [newKeyword, setNewKeyword] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = keywords.findIndex((k) => k.id === active.id);
      const newIndex = keywords.findIndex((k) => k.id === over.id);
      reorderKeywords(type, arrayMove(keywords, oldIndex, newIndex));
    }
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim()) {
      addKeyword({ text: newKeyword.trim(), type });
      setNewKeyword('');
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

    if (lines.length > 1) {
      e.preventDefault();
      lines.forEach((line) => {
        addKeyword({ text: line, type });
      });
    }
  };

  const colorClasses = {
    accent: {
      bg: 'bg-accent-50 dark:bg-accent-900/20',
      border: 'border-accent-200 dark:border-accent-800',
      text: 'text-accent-700 dark:text-accent-300',
      badge: 'bg-accent-100 text-accent-700 dark:bg-accent-900 dark:text-accent-300',
    },
    danger: {
      bg: 'bg-danger-50 dark:bg-danger-900/20',
      border: 'border-danger-200 dark:border-danger-800',
      text: 'text-danger-700 dark:text-danger-300',
      badge: 'bg-danger-100 text-danger-700 dark:bg-danger-900 dark:text-danger-300',
    },
  };

  const colors = colorClasses[color];

  return (
    <div className={clsx('card overflow-hidden', colors.border)}>
      <div className={clsx('px-4 py-3 border-b', colors.bg, colors.border)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className={clsx('font-semibold', colors.text)}>{title}</h3>
            <span className={clsx('badge', colors.badge)}>
              {keywords.length}
            </span>
          </div>
          {keywords.length > 0 && (
            <button
              onClick={() => clearKeywords(type)}
              className="text-slate-400 hover:text-danger-500 transition-colors"
              title={`Clear all ${type} keywords`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Add keyword input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
            onPaste={handlePaste}
            placeholder={`Add ${type} keyword...`}
            className="input flex-1"
          />
          <button
            onClick={handleAddKeyword}
            disabled={!newKeyword.trim()}
            className={clsx(
              'btn',
              color === 'accent' ? 'btn-success' : 'btn-danger'
            )}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Keywords list */}
        <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-2">
          {keywords.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={keywords.map((k) => k.id)}
                strategy={verticalListSortingStrategy}
              >
                {keywords.map((keyword) => (
                  <SortableKeyword
                    key={keyword.id}
                    keyword={keyword}
                    onRemove={() => removeKeyword(keyword.id)}
                    onUpdate={(text) => updateKeyword(keyword.id, text)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <p className="text-sm">No keywords added yet</p>
              <p className="text-xs mt-1">Paste multiple lines to add in bulk</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function KeywordManager() {
  const {
    papers,
    inclusionKeywords,
    exclusionKeywords,
    settings,
    setResults,
    setActiveTab,
    isProcessing,
    setIsProcessing,
    setProgress,
    progress,
    clearKeywords,
    updateSettings,
  } = useStore();

  const [error, setError] = useState<string | null>(null);

  const canAnalyze =
    papers.length > 0 &&
    (inclusionKeywords.length > 0 || exclusionKeywords.length > 0);

  const handleAnalyze = async () => {
    if (!canAnalyze) return;

    setIsProcessing(true);
    setError(null);

    try {
      const results = await analyzePapers(
        papers,
        inclusionKeywords,
        exclusionKeywords,
        {
          caseSensitive: settings.caseSensitive,
          matchWholeWords: settings.matchWholeWords,
          searchInAbstract: settings.searchInAbstract,
          onProgress: (current, total) => {
            setProgress({
              current,
              total,
              stage: 'analyzing',
              message: `Analyzing paper ${current.toLocaleString()} of ${total.toLocaleString()}...`,
            });
          },
        }
      );

      setResults(results);
      setActiveTab('results');
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setIsProcessing(false);
      setProgress(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Keywords Manager
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Define inclusion and exclusion keywords for filtering
          </p>
        </div>

        <button
          onClick={() => clearKeywords()}
          className="btn-ghost text-slate-500 hover:text-danger-500"
        >
          <Trash2 className="w-4 h-4" />
          <span>Clear All</span>
        </button>
      </div>

      {/* Status check */}
      {papers.length === 0 && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <p className="text-amber-800 dark:text-amber-200">
            Please upload a papers file first before adding keywords.
          </p>
          <button
            onClick={() => setActiveTab('upload')}
            className="ml-auto btn-secondary text-sm"
          >
            <Upload className="w-4 h-4" />
            Upload File
          </button>
        </div>
      )}

      {/* Keywords grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <KeywordList
          title="Inclusion Keywords"
          keywords={inclusionKeywords}
          type="inclusion"
          color="accent"
        />
        <KeywordList
          title="Exclusion Keywords"
          keywords={exclusionKeywords}
          type="exclusion"
          color="danger"
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
          <AlertCircle className="w-5 h-5 text-danger-600" />
          <p className="text-danger-800 dark:text-danger-200">{error}</p>
        </div>
      )}

      {/* Progress bar */}
      {isProcessing && progress && (
        <div className="card p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {progress.message}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Analysis Settings */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-4 h-4 text-slate-500" />
          <h3 className="font-medium text-slate-700 dark:text-slate-300">Analysis Settings</h3>
        </div>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-2">
              <FileTextIcon className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600 dark:text-slate-400">Search in Abstracts</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.searchInAbstract}
              onClick={() => updateSettings({ searchInAbstract: !settings.searchInAbstract })}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.searchInAbstract ? 'bg-primary-500' : 'bg-slate-300 dark:bg-slate-600'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.searchInAbstract ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-slate-600 dark:text-slate-400">Case Sensitive</span>
            <button
              type="button"
              role="switch"
              aria-checked={settings.caseSensitive}
              onClick={() => updateSettings({ caseSensitive: !settings.caseSensitive })}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.caseSensitive ? 'bg-primary-500' : 'bg-slate-300 dark:bg-slate-600'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.caseSensitive ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-slate-600 dark:text-slate-400">Match Whole Words Only</span>
            <button
              type="button"
              role="switch"
              aria-checked={settings.matchWholeWords}
              onClick={() => updateSettings({ matchWholeWords: !settings.matchWholeWords })}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                settings.matchWholeWords ? 'bg-primary-500' : 'bg-slate-300 dark:bg-slate-600'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  settings.matchWholeWords ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </label>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          {canAnalyze ? (
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-accent-500" />
              Ready to analyze {papers.length.toLocaleString()} papers with{' '}
              {inclusionKeywords.length + exclusionKeywords.length} keywords
            </span>
          ) : (
            <span>Add keywords to start analysis</span>
          )}
        </div>

        <button
          onClick={handleAnalyze}
          disabled={!canAnalyze || isProcessing}
          className="btn-primary"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run Analysis
            </>
          )}
        </button>
      </div>
    </div>
  );
}
